import React, { useState, useEffect } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import ChecklistItem from './ChecklistItem';
import OnboardingProgress from './OnboardingProgress';
import OnboardingCelebration from './OnboardingCelebration';
import useTour from '../hooks/useTour';
import { markAllCompleted } from '../redux/onboardingActions';
import {
  selectAvailableTours,
  selectCompletedCount,
  selectTotalCount,
  selectProgressPercent,
} from '../redux/onboardingSelectors';
import { trackChecklistViewed, trackChecklistSkipped, trackAllCompleted } from '../analytics/onboardingEvents';
import { getRegionContent } from '../utils/tourUtils';

// Checklist item metadata — icon, i18n keys
const TOUR_META = {
  dashboard: { icon: '📊', titleKey: 'onboarding_item.dashboard.title', subtitleKey: 'onboarding_item.dashboard.subtitle' },
  search: { icon: '🔍', titleKey: 'onboarding_item.search.title', subtitleKey: 'onboarding_item.search.subtitle' },
  chat: { icon: '💬', titleKey: 'onboarding_item.chat.title', subtitleKey: 'onboarding_item.chat.subtitle' },
  contract: { icon: '📄', titleKey: 'onboarding_item.contract.title', subtitleKey: 'onboarding_item.contract.subtitle' },
  legalNews: { icon: '📰', titleKey: 'onboarding_item.legalNews.title', subtitleKey: 'onboarding_item.legalNews.subtitle' },
  annonceLegale: { icon: '🏢', titleKey: 'onboarding_item.annonceLegale.title', subtitleKey: 'onboarding_item.annonceLegale.subtitle' },
  settings: { icon: '⚙️', titleKey: 'onboarding_item.settings.title', subtitleKey: 'onboarding_item.settings.subtitle' },
};

// Default subtitles (EN fallback)
const DEFAULT_TITLES = {
  dashboard: { title: 'Explore Your Dashboard', subtitle: 'Get familiar with your command center' },
  search: { title: 'Master Legal Search', subtitle: 'Find laws and court decisions instantly' },
  chat: { title: 'Chat with AI Assistant', subtitle: 'Ask legal questions, get sourced answers' },
  contract: { title: 'Contract Management', subtitle: 'Create, review, and sign contracts' },
  legalNews: { title: 'Legal News & Monitoring', subtitle: 'Stay updated on regulatory changes' },
  annonceLegale: { title: 'Business Announcements', subtitle: 'Search companies and legal publications' },
  settings: { title: 'Personalize Your AI', subtitle: 'Customize AI responses for your organization' },
};

const OnboardingDashboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startTour } = useTour();

  const user = useSelector((state) => state.auth.user?.user);
  const countryPref = useSelector((state) => state.user?.countryPreference);
  const availableTours = useSelector(selectAvailableTours);
  const completedCount = useSelector(selectCompletedCount);
  const totalCount = useSelector(selectTotalCount);
  const progressPct = useSelector(selectProgressPercent);

  const [showCelebration, setShowCelebration] = useState(false);
  const [prevCompleted, setPrevCompleted] = useState(completedCount);

  const regionContent = getRegionContent(countryPref);

  // Track checklist view
  useEffect(() => {
    trackChecklistViewed(completedCount, totalCount);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect all tours completed → show celebration
  useEffect(() => {
    if (completedCount > prevCompleted && progressPct === 100) {
      setShowCelebration(true);
      trackAllCompleted('tours', 0);
    }
    setPrevCompleted(completedCount);
  }, [completedCount, progressPct, prevCompleted]);

  const handleSkipAll = () => {
    trackChecklistSkipped(completedCount, totalCount);
    trackAllCompleted('skip', 0);
    dispatch(markAllCompleted());
    navigate('/');
  };

  const handleCelebrationDismiss = () => {
    setShowCelebration(false);
    dispatch(markAllCompleted());
    navigate('/');
  };

  const getSubtitle = (tourId) => {
    const meta = TOUR_META[tourId];
    // Region-aware subtitles for search and chat
    if ((tourId === 'search' || tourId === 'chat') && regionContent.countryCode) {
      const regionKey = `${meta.subtitleKey}_${regionContent.countryCode}`;
      const regionResult = t(regionKey, '');
      if (regionResult && regionResult !== regionKey) return regionResult;
    }
    return t(meta.subtitleKey, DEFAULT_TITLES[tourId]?.subtitle || '');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 100px)',
        py: { xs: 3, sm: 5 },
        px: 2,
      }}
    >
      {/* Header */}
      <Typography
        variant="h3"
        sx={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontWeight: 500,
          mb: 1,
          textAlign: 'center',
          fontSize: { xs: 28, sm: 34 },
        }}
      >
        {t('onboarding_checklist.title', { firstName: user?.first_name || '' })}
      </Typography>
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', mb: 4, textAlign: 'center', maxWidth: 480 }}
      >
        {t('onboarding_checklist.subtitle', 'Complete these guides to master your legal workspace')}
      </Typography>

      {/* Progress */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <OnboardingProgress completed={completedCount} total={totalCount} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('onboarding_checklist.progress', { count: completedCount, total: totalCount })}
        </Typography>
      </Box>

      {/* Checklist card */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 560,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? '0 4px 24px rgba(0,0,0,0.4)'
              : '0 4px 16px rgba(0,0,0,0.06)',
        }}
      >
        {/* Gold top accent */}
        <Box sx={{ height: 3, bgcolor: '#c9a96e' }} />

        {availableTours.map((tour, idx) => {
          const meta = TOUR_META[tour.id];
          return (
            <ChecklistItem
              key={tour.id}
              title={t(meta.titleKey, DEFAULT_TITLES[tour.id]?.title || tour.id)}
              subtitle={getSubtitle(tour.id)}
              completed={tour.completed}
              onStart={() => startTour(tour.id)}
              isLast={idx === availableTours.length - 1}
            />
          );
        })}
      </Box>

      {/* Skip button */}
      <Button
        onClick={handleSkipAll}
        sx={{
          mt: 3,
          color: 'text.secondary',
          textTransform: 'none',
          fontSize: 13,
          '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
        }}
      >
        {t('onboarding_checklist.mark_all', 'Mark all as completed')}
      </Button>

      {/* Celebration overlay */}
      {showCelebration && <OnboardingCelebration onDismiss={handleCelebrationDismiss} />}
    </Box>
  );
};

export default OnboardingDashboard;
